import React from 'react';
import { useIntl } from '@atlassian/jira-intl';
import { useDateFormatter } from '@atlassian/jira-portfolio-3-common';
import { ISSUE_FIELDS } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/issues/types';
import { colorByLabelsMap } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/view-settings/colour-by/utils';
import { getOptionValueLabels } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/view-settings/sort-by/utils';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import { colourByOptions } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/colours';
import { GROUPING } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { isRoadmapGroupedByCustomField } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/custom-fields';
import {
	getGroupByCustomFieldLabel,
	getGroupByStandardFieldLabel,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/grouping';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
import FilterViewInformation from './filter';
import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props, ViewInfoGroupContentProps } from './types';

export const ViewInfoGroup = ({ title, items }: ViewInfoGroupContentProps) => (
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
	<div className={styles.group}>
		{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
		<span className={styles.groupTitle}>{title}:</span>
		{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
		<span className={styles.groupContent}>
			{items.map((item, i) => (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				<span key={`item-${i}`} className={styles.groupContentItem}>
					{item}
				</span>
			))}
		</span>
	</div>
);

export default function ViewInformation({
	hierarchyRange,
	timelineRange,
	groupBy,
	colorBy,
	rollupSettings,
	sorting,
	customFields,
	planningUnit,
	filterCount,
	warningSettings,
	showFullHierarchy,
	FilterViewInformationComponent = FilterViewInformation,
}: Props) {
	const intl = useIntl();
	const { formatTimestamp } = useDateFormatter();

	const { start, end } = timelineRange;

	const rollup = ((): string | null | undefined => {
		const { showingRolledUpDate, showRolledUpOthers } = rollupSettings;
		if (showingRolledUpDate && showRolledUpOthers) {
			return intl.formatMessage(messages.rollupAll);
		}
		if (showingRolledUpDate) {
			return intl.formatMessage(commonMessages.dates);
		}
		if (showRolledUpOthers) {
			return intl.formatMessage(messages.rollupOthers);
		}
		return null;
	})();

	const sortLabel = ((): string => {
		const sortOptionLabels: Record<string, string> = {
			...getOptionValueLabels(intl, customFields, planningUnit),
			[ISSUE_FIELDS.RANK]: intl.formatMessage(messages.rank),
		};
		return sortOptionLabels[sorting.field];
	})();

	const colorByLabel = ((): string => {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		if (colorByLabelsMap[colorBy as keyof typeof colorByLabelsMap]) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return intl.formatMessage(colorByLabelsMap[colorBy as keyof typeof colorByLabelsMap]);
		}
		const customField = customFields.find((field) => `${field.id}` === colorBy);
		if (customField) {
			return customField.title;
		}
		return '';
	})();

	const groupByLabel = (() => {
		if (isRoadmapGroupedByCustomField(groupBy)) {
			return getGroupByCustomFieldLabel(groupBy, customFields);
		}
		return intl.formatMessage(getGroupByStandardFieldLabel(groupBy));
	})();

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<section className={styles.viewInformation}>
			<ViewInfoGroup
				title={intl.formatMessage(messages.hierarchy)}
				items={[
					// eslint-disable-next-line react/jsx-key
					<>
						<strong>{hierarchyRange.from}</strong> {intl.formatMessage(messages.hierarchySeparator)}{' '}
						<strong>{hierarchyRange.to}</strong>
						{showFullHierarchy && (
							<>
								{' '}
								<span>({intl.formatMessage(messages.showFullHierarchy)})</span>
							</>
						)}
					</>,
				]}
			/>
			<ViewInfoGroup
				title={intl.formatMessage(messages.view)}
				items={[
					...(groupBy !== GROUPING.NONE
						? [
								// eslint-disable-next-line react/jsx-key
								<>
									<strong>{intl.formatMessage(commonMessages.groupBy)}</strong> ({groupByLabel})
								</>,
							]
						: []),
					...(colorBy !== colourByOptions.NONE
						? [
								// eslint-disable-next-line react/jsx-key
								<>
									<strong>{intl.formatMessage(messages.colorBy)}</strong> ({colorByLabel})
								</>,
							]
						: []),
					...(isDefined(rollup)
						? [
								// eslint-disable-next-line react/jsx-key
								<>
									<strong>{intl.formatMessage(messages.rollup)}</strong> ({rollup})
								</>,
							]
						: []),
					// eslint-disable-next-line react/jsx-key
					<>
						<strong>{intl.formatMessage(messages.sortBy)}</strong> ({sortLabel})
					</>,
				]}
			/>
			<ViewInfoGroup
				title={intl.formatMessage(commonMessages.dateRange)}
				items={[
					// eslint-disable-next-line react/jsx-key
					<>
						<strong>
							{formatTimestamp(start)} - {formatTimestamp(end)}
						</strong>
					</>,
				]}
			/>

			<ViewInfoGroup
				title={intl.formatMessage(messages.filters, {
					filtersApplied: filterCount,
				})}
				items={[
					filterCount > 0 ? (
						<FilterViewInformationComponent />
					) : (
						<strong>{intl.formatMessage(messages.noFilterApplied)}</strong>
					),
				]}
			/>

			<ViewInfoGroup
				title={intl.formatMessage(messages.warnings)}
				items={[
					// eslint-disable-next-line react/jsx-key
					<>
						<strong>
							{intl.formatMessage(
								warningSettings.showWarning ? messages.warningsEnabled : messages.warningsDisabled,
							)}
						</strong>
					</>,
				]}
			/>
		</section>
	);
}
